import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';

/**
 * On iOS WKWebView and some Android WebViews, binary objects from the camera,
 * native bridges, or another JS realm may not pass `instanceof Blob` / `instanceof File`
 * while still being valid for FormData multipart uploads.
 */
export function isBlobLike(value: unknown): value is Blob {
  if (value == null || typeof value !== 'object') {
    return false;
  }
  if (value instanceof Blob) {
    return true;
  }
  const tag = Object.prototype.toString.call(value);
  if (tag === '[object Blob]' || tag === '[object File]') {
    return true;
  }
  const b = value as Partial<Blob>;
  return (
    typeof b.size === 'number' &&
    (typeof b.arrayBuffer === 'function' ||
      typeof b.slice === 'function' ||
      typeof (b as { stream?: unknown }).stream === 'function')
  );
}

/** Rejects non-MIME strings (e.g. "[object Object]") from bridged Blobs. */
export function sanitizeBlobMimeType(type: unknown): string | undefined {
  if (typeof type !== 'string') {
    return undefined;
  }
  const t = type.trim();
  if (!t || t === '[object Object]' || t === '[object object]') {
    return undefined;
  }
  // RFC 2045 token / MIME subset
  if (!/^[\w!#$%&'*+.^_`|~-]+\/[\w!#$%&'*+.^_`|~-]+$/i.test(t)) {
    return undefined;
  }
  return t;
}

export function asTrimmedString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (value == null) {
    return '';
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

export function extensionFromFilename(name: unknown): string | undefined {
  const safeName = asTrimmedString(name);
  if (!safeName) {
    return undefined;
  }
  return safeName.split('.').pop()?.toLowerCase();
}

/** Coerce picker/preview values into a backend-compatible MIME type string. */
export function resolveDemandPreuveMime(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const sanitized = sanitizeBlobMimeType(candidate);
    if (sanitized) {
      return sanitized;
    }

    const trimmed = asTrimmedString(candidate).toLowerCase();
    if (trimmed === 'video') {
      return 'video/mp4';
    }
    if (trimmed === 'image') {
      return 'image/jpeg';
    }
  }

  return 'image/jpeg';
}

export function describeUploadCandidate(value: unknown): Record<string, unknown> {
  if (value == null) {
    return { present: false };
  }

  const candidate = value as File & { path?: unknown };
  const uploadMeta = isBlobLike(value) ? getUploadFileMetadata(value) : null;
  const safeName = uploadMeta?.fileName || asTrimmedString(candidate.name);
  const safeType = uploadMeta?.mimeType || asTrimmedString(candidate.type);
  const size = Number(candidate.size);

  return {
    present: true,
    objectTag: Object.prototype.toString.call(value),
    name: candidate.name,
    nameType: typeof candidate.name,
    safeName,
    type: candidate.type,
    typeType: typeof candidate.type,
    safeType,
    uploadMeta,
    size: candidate.size,
    sizeType: typeof candidate.size,
    numericSize: Number.isFinite(size) ? size : null,
    isBlobLike: isBlobLike(value),
    mediaType: getDemandMediaType({ name: safeName, type: safeType }),
    backendAccepted: isBackendAcceptedDemandPreuveFile(
      uploadMeta ? (value as Blob) : { name: safeName, type: safeType }
    ),
    preparedValid: isBlobLike(value) ? isValidPreparedUploadFile(value) : false,
    heicLike: isHeicLikeFile({ name: safeName, type: safeType }),
    pathPreview:
      typeof candidate.path === "string"
        ? `${candidate.path.slice(0, 32)}...`
        : undefined,
  };
}

export function logDemandPreuveUpload(
  step: string,
  detail?: Record<string, unknown>
): void {
  console.log("[DemandePreuveUpload]", step, detail ?? {});
}

export function extensionFromMimeType(mimeType: unknown): string {
  const safeMime = resolveDemandPreuveMime(mimeType);
  return backendPreuveExtensionFromMime(safeMime) || 'jpg';
}

/** Filename for FormData when the value is a Blob that may not be a File instance. */
export function formDataBlobFileName(blob: Blob, index: number): string {
  const { fileName } = getUploadFileMetadata(blob, index);
  if (fileName && /\.[a-z0-9]{2,8}$/i.test(fileName)) {
    return fileName;
  }

  const safe = sanitizeBlobMimeType(blob.type);
  if (safe === 'image/png') {
    return `image_${index}.png`;
  }
  if (safe === 'image/jpeg' || safe === 'image/jpg') {
    return `image_${index}.jpg`;
  }
  if (safe?.startsWith('video/')) {
    const ext = backendPreuveExtensionFromMime(safe);
    return `video_${index}.${ext || 'mp4'}`;
  }
  return `file_${index}.jpg`;
}

type UploadFileMetadataCarrier = Blob & {
  __uploadFileName?: unknown;
  __uploadMimeType?: unknown;
};

/** Read filename/MIME from a real File or a WKWebView-safe Blob fallback. */
export function getUploadFileMetadata(
  blob: Blob,
  index = 0
): { fileName: string; mimeType: string } {
  const carrier = blob as UploadFileMetadataCarrier & File;
  const syntheticName = asTrimmedString(carrier.__uploadFileName);
  const rawName = asTrimmedString(carrier.name);
  const fileName =
    (syntheticName && syntheticName.length > 0 ? syntheticName : "") ||
    (rawName && rawName.length > 0 ? rawName : "") ||
    `file_${index}.jpg`;

  const syntheticMime = sanitizeBlobMimeType(carrier.__uploadMimeType);
  const mimeType =
    syntheticMime ||
    sanitizeBlobMimeType(blob.type) ||
    resolveDemandPreuveMime(extensionFromFilename(fileName));

  return { fileName, mimeType };
}

function attachUploadFileMetadata(
  blob: Blob,
  fileName: string,
  mimeType: string
): void {
  const carrier = blob as UploadFileMetadataCarrier;
  try {
    Object.defineProperty(carrier, "name", {
      value: fileName,
      configurable: true,
      enumerable: true,
    });
  } catch {
    // ignore
  }
  try {
    Object.defineProperty(carrier, "__uploadFileName", {
      value: fileName,
      configurable: true,
      enumerable: false,
    });
  } catch {
    // ignore
  }
  try {
    Object.defineProperty(carrier, "__uploadMimeType", {
      value: mimeType,
      configurable: true,
      enumerable: false,
    });
  } catch {
    // ignore
  }
}

function isNativeUploadFileValid(
  candidate: Blob,
  fileName: string,
  mimeType: string
): boolean {
  if (!isBlobLike(candidate) || Number(candidate.size) <= 0) {
    return false;
  }

  const resolvedName = asTrimmedString((candidate as File).name);
  if (resolvedName !== fileName) {
    return false;
  }

  const resolvedMime = sanitizeBlobMimeType(candidate.type);
  return !!resolvedMime && resolvedMime === mimeType;
}

/**
 * WKWebView can return malformed File objects from `new File([buffer], name)`.
 * Fall back to a typed Blob with synthetic filename metadata for FormData.
 */
export function createCapacitorSafeUploadFile(
  parts: BlobPart[],
  fileName: string,
  mimeType: string
): File {
  const safeMime = resolveDemandPreuveMime(mimeType);
  const safeName = ensureBackendPreuveFilename(
    asTrimmedString(fileName) || "upload.jpg",
    safeMime
  );

  try {
    const candidate = new File(parts, safeName, { type: safeMime });
    if (isNativeUploadFileValid(candidate, safeName, safeMime)) {
      return candidate;
    }
    logDemandPreuveUpload("createUploadFile:fallback_blob", {
      reason: "invalid_native_file_constructor",
      expectedName: safeName,
      expectedMime: safeMime,
      ...describeUploadCandidate(candidate),
    });
  } catch (error) {
    logDemandPreuveUpload("createUploadFile:fallback_blob", {
      reason: "native_file_constructor_threw",
      expectedName: safeName,
      expectedMime: safeMime,
      error,
    });
  }

  const blob = new Blob(parts, { type: safeMime });
  attachUploadFileMetadata(blob, safeName, safeMime);
  return blob as File;
}

function isBackendAcceptedDemandPreuveDescriptor(file: {
  name?: unknown;
  type?: unknown;
}): boolean {
  const mime = sanitizeBlobMimeType(file.type)?.toLowerCase();
  if (mime && backendPreuveExtensionFromMime(mime)) {
    return true;
  }

  const extension = extensionFromFilename(file.name);
  if (!extension) {
    return false;
  }
  return (
    DEMAND_IMAGE_EXTENSIONS.has(extension) ||
    DEMAND_VIDEO_EXTENSIONS.has(extension)
  );
}

export function isValidPreparedUploadFile(file: Blob): boolean {
  if (!isBlobLike(file) || Number(file.size) <= 0) {
    return false;
  }

  const { fileName, mimeType } = getUploadFileMetadata(file);
  return isBackendAcceptedDemandPreuveDescriptor({ name: fileName, type: mimeType });
}

/**
 * Native FormData.append(,,filename) on WKWebView can reject **bridged** File/Blob
 * from plugins even when JS reports `instanceof Blob`. Always clone with `new Blob([x])`
 * so the binary body is allocated in this JS realm.
 */
export function toNativeBlobForFormData(part: unknown): Blob {
  if (part == null || typeof part !== 'object') {
    return new Blob();
  }

  const cloneIntoCurrentRealm = (blob: Blob): Blob => {
    const t = sanitizeBlobMimeType(blob.type);
    try {
      return new Blob([blob], { type: t || undefined });
    } catch {
      try {
        const sliced = blob.slice(0, blob.size, blob.type);
        const st = sanitizeBlobMimeType(sliced.type);
        return new Blob([sliced], { type: st || undefined });
      } catch {
        return new Blob();
      }
    }
  };

  if (part instanceof Blob) {
    return cloneIntoCurrentRealm(part);
  }

  const b = part as Blob;
  try {
    if (typeof b.slice === 'function') {
      const sliced = b.slice(0, b.size, b.type);
      if (sliced instanceof Blob) {
        return cloneIntoCurrentRealm(sliced);
      }
    }
  } catch {
    // ignore
  }
  try {
    const t = sanitizeBlobMimeType(b.type);
    return new Blob([b], { type: t || undefined });
  } catch {
    const t = sanitizeBlobMimeType(b.type);
    return new Blob([], { type: t || undefined });
  }
}

export function objectToFormData(
  obj: Record<string, any>,
  formData = new FormData()
): FormData {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      const propName = key;
      if (value instanceof Array) {
        for (let i = 0; i < value.length; i++) {
          const item = value[i];
          if (item == null) continue;
          const arrayKey = `${propName}[${i}]`;
          appendFormDataValue(formData, arrayKey, item, i);
        }
      } else {
        appendFormDataValue(formData, propName, value, 0);
      }
    }
  }
  return formData;
}

function appendFormDataValue(
  formData: FormData,
  key: string,
  value: any,
  index: number
): void {
  if (value == null || value === "") {
    return;
  }

  const uploadValue = unwrapUploadValue(value);
  if (isBlobLike(uploadValue)) {
    appendBlobValue(formData, key, uploadValue, index);
    return;
  }

  if (typeof uploadValue === "object") {
    return;
  }

  formData.append(key, String(uploadValue));
}

function unwrapUploadValue(value: any): any {
  if (value?.file) {
    return value.file;
  }

  return value;
}

function appendBlobValue(
  formData: FormData,
  key: string,
  value: Blob,
  index: number
): void {
  const filename = formDataBlobFileName(value, index);
  const body = toNativeBlobForFormData(value);
  formData.append(key, body, filename);
}

export function isImageOrVideo(url: unknown): string {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "svg"];
  const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm"];
  const extension = extensionFromFilename(url);

  if (!extension) {
    return "unknown";
  }

  if (imageExtensions.includes(extension)) {
    return "image";
  } else if (videoExtensions.includes(extension)) {
    return "video";
  } else {
    return "unknown";
  }
}
export function getVideoMimeType(url: unknown): string {
  const extension = extensionFromFilename(url);
  const mimeTypes: { [key: string]: string } = {
    mp4: "video/mp4",
    avi: "video/x-msvideo",
    mov: "video/quicktime",
    wmv: "video/x-ms-wmv",
    flv: "video/x-flv",
    mkv: "video/x-matroska",
    webm: "video/webm",
  };

  return mimeTypes[extension!] || "application/octet-stream";
}
export function getMimeType(url: unknown): string {
  const extension = extensionFromFilename(url);
  const mimeTypes: { [key: string]: string } = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    bmp: "image/bmp",
    tiff: "image/tiff",
    svg: "image/svg+xml",
    mp4: "video/mp4",
    avi: "video/x-msvideo",
    mov: "video/quicktime",
    wmv: "video/x-ms-wmv",
    flv: "video/x-flv",
    mkv: "video/x-matroska",
    webm: "video/webm",
  };

  return mimeTypes[extension!] || "application/octet-stream";
}

const DEMAND_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
]);

const DEMAND_VIDEO_MIME_TYPES = new Set([
  'video/mp4',
  'video/x-msvideo',
  'video/quicktime',
  'video/avi',
  // Non-standard aliases occasionally reported by mobile pickers.
  'video/mov',
]);

const DEMAND_IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
]);

const DEMAND_VIDEO_EXTENSIONS = new Set([
  'mp4',
  'avi',
  'mov',
]);

/** Matches Laravel validation on `demande-image-preuve` endpoint. */
const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTENSIONS = new Set(['heic', 'heif']);

export function isHeicLikeFile(file: { name?: unknown; type?: unknown }): boolean {
  const mime = asTrimmedString(file.type).toLowerCase();
  if (HEIC_MIME_TYPES.has(mime)) {
    return true;
  }
  const extension = extensionFromFilename(file.name);
  return !!extension && HEIC_EXTENSIONS.has(extension);
}

export function mimeTypeFromDataUrl(dataUrl: string): string | undefined {
  const match = dataUrl.match(/^data:([^;,]+)/i);
  return match ? sanitizeBlobMimeType(match[1]) : undefined;
}

function backendPreuveExtensionFromMime(mime: string): string | null {
  switch (mime.toLowerCase()) {
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/gif':
      return 'gif';
    case 'video/mp4':
      return 'mp4';
    case 'video/quicktime':
    case 'video/mov':
      return 'mov';
    case 'video/x-msvideo':
    case 'video/avi':
      return 'avi';
    default:
      return null;
  }
}

export function ensureBackendPreuveFilename(
  name: unknown,
  mimeType: unknown
): string {
  const safeName = asTrimmedString(name) || 'upload';
  const safeMime = resolveDemandPreuveMime(mimeType);
  const extension = backendPreuveExtensionFromMime(safeMime);
  if (!extension) {
    return safeName;
  }
  const baseName = (safeName.replace(/\.[^.]+$/, '') || 'upload').trim() || 'upload';
  return `${baseName}.${extension}`;
}

export function isBackendAcceptedDemandPreuveFile(
  file: { name?: unknown; type?: unknown } | Blob
): boolean {
  if (isBlobLike(file)) {
    const meta = getUploadFileMetadata(file);
    return isBackendAcceptedDemandPreuveDescriptor({
      name: meta.fileName,
      type: meta.mimeType,
    });
  }

  return isBackendAcceptedDemandPreuveDescriptor(file);
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode image"));
    img.src = src;
  });
}

/** Hard ceiling per demand-preuve file (images + videos) shown to the user. */
export const DEMAND_PREUVE_MAX_FILE_BYTES = 100 * 1024 * 1024;

/** Images above this size are auto-compressed before upload (camera always compressed). */
const DEMAND_PREUVE_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
const DEMAND_PREUVE_MAX_IMAGE_EDGE = 4096;
const DEMAND_PREUVE_JPEG_QUALITY = 0.82;

function scaleImageDimensions(
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } {
  if (width <= maxEdge && height <= maxEdge) {
    return { width, height };
  }
  if (width >= height) {
    return {
      width: maxEdge,
      height: Math.max(1, Math.round((height * maxEdge) / width)),
    };
  }
  return {
    width: Math.max(1, Math.round((width * maxEdge) / height)),
    height: maxEdge,
  };
}

/** Camera captures are often huge; compress to avoid gateway/backend 502 on upload. */
export async function compressDemandPreuveImageIfNeeded(
  file: File,
  options: { force?: boolean } = {}
): Promise<File> {
  const mediaType = getDemandMediaType(file);
  if (!options.force && mediaType !== "image") {
    return file;
  }

  const size = Number(file.size);
  const exceedsByteLimit =
    Number.isFinite(size) && size > DEMAND_PREUVE_UPLOAD_MAX_BYTES;
  if (!options.force && !exceedsByteLimit) {
    return file;
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(objectUrl);
    const sourceWidth = img.naturalWidth || img.width;
    const sourceHeight = img.naturalHeight || img.height;
    const needsResize =
      sourceWidth > DEMAND_PREUVE_MAX_IMAGE_EDGE ||
      sourceHeight > DEMAND_PREUVE_MAX_IMAGE_EDGE;

    if (!options.force && !exceedsByteLimit && !needsResize) {
      return file;
    }

    const { width, height } = scaleImageDimensions(
      sourceWidth,
      sourceHeight,
      DEMAND_PREUVE_MAX_IMAGE_EDGE
    );
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }
    context.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", DEMAND_PREUVE_JPEG_QUALITY)
    );
    if (!blob?.size) {
      return file;
    }

    const baseName =
      asTrimmedString(file.name).replace(/\.[^.]+$/i, "").trim() ||
      `photo_${Date.now()}`;
    const compressed = createCapacitorSafeUploadFile(
      [blob],
      `${baseName}.jpg`,
      "image/jpeg"
    );

    logDemandPreuveUpload("compress:image", {
      reason: options.force ? "camera_forced" : "size_or_dimensions",
      originalSize: size,
      compressedSize: compressed.size,
      originalDimensions: `${sourceWidth}x${sourceHeight}`,
      compressedDimensions: `${width}x${height}`,
    });

    return compressed;
  } catch (error) {
    logDemandPreuveUpload("compress:image_failed", {
      error,
      ...describeUploadCandidate(file),
    });
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** iOS gallery often returns HEIC; backend only accepts jpeg/png/gif. */
export async function convertHeicLikeFileToJpeg(file: File): Promise<File | null> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(objectUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const context = canvas.getContext('2d');
    if (!context || canvas.width <= 0 || canvas.height <= 0) {
      return null;
    }
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.92)
    );
    if (!blob?.size) {
      return null;
    }
    const baseName =
      asTrimmedString(file.name).replace(/\.[^.]+$/i, '').trim() ||
      `photo_${Date.now()}`;
    return createCapacitorSafeUploadFile([blob], `${baseName}.jpg`, "image/jpeg");
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function getDemandMediaType(file: {
  name?: unknown;
  type?: unknown;
}): "image" | "video" | null {
  const mime = asTrimmedString(file.type).toLowerCase();
  if (DEMAND_IMAGE_MIME_TYPES.has(mime)) {
    return "image";
  }
  if (DEMAND_VIDEO_MIME_TYPES.has(mime)) {
    return "video";
  }

  const extension = extensionFromFilename(file.name);
  if (!extension) {
    return null;
  }
  if (DEMAND_IMAGE_EXTENSIONS.has(extension)) {
    return "image";
  }
  if (DEMAND_VIDEO_EXTENSIONS.has(extension)) {
    return "video";
  }
  return null;
}

export function isAcceptedDemandMediaFile(file: {
  name?: string;
  type?: string;
}): boolean {
  return getDemandMediaType(file) !== null;
}

export function normalizeDemandMediaFile(file: File): File {
  const mediaType = getDemandMediaType(file);
  if (!mediaType) {
    return file;
  }

  const mime = asTrimmedString(file.type).toLowerCase();
  const hasKnownMime =
    mediaType === "image"
      ? DEMAND_IMAGE_MIME_TYPES.has(mime)
      : DEMAND_VIDEO_MIME_TYPES.has(mime);
  if (hasKnownMime) {
    return file;
  }

  const safeName = asTrimmedString(file.name) || "upload";
  const extension = extensionFromFilename(safeName);
  const correctedMime = extension
    ? getMimeType(`file.${extension}`)
    : mediaType === "image"
      ? "image/jpeg"
      : "video/mp4";

  if (correctedMime === "application/octet-stream") {
    const fallbackMime = mediaType === "image" ? "image/jpeg" : "video/mp4";
    return createCapacitorSafeUploadFile([file], safeName, fallbackMime);
  }

  return createCapacitorSafeUploadFile([file], safeName, correctedMime);
}

/** Camera/HTML capture can omit MIME type and extension; coerce before validation. */
export function coerceCameraCaptureFile(file: File): File {
  const normalized = normalizeDemandMediaFile(file);
  if (getDemandMediaType(normalized)) {
    return normalized;
  }

  const safeName = asTrimmedString(normalized.name);
  const extension = extensionFromFilename(safeName);
  const fileName =
    extension && DEMAND_IMAGE_EXTENSIONS.has(extension)
      ? safeName
      : `photo_${Date.now()}.jpg`;

  return createCapacitorSafeUploadFile([normalized], fileName, "image/jpeg");
}

/** Clone picker/camera blobs into this JS realm before multipart upload. */
export async function materializeUploadFile(file: File): Promise<File | null> {
  logDemandPreuveUpload("materialize:start", describeUploadCandidate(file));

  const normalized = normalizeDemandMediaFile(file);
  const normalizedSize = Number(normalized.size);
  if (!isBlobLike(normalized) || normalizedSize <= 0) {
    logDemandPreuveUpload("materialize:reject", {
      reason: !isBlobLike(normalized) ? "not_blob_like" : "empty_size",
      normalizedSize,
      ...describeUploadCandidate(normalized),
    });
    return null;
  }

  const fileName =
    getUploadFileMetadata(normalized).fileName ||
    formDataBlobFileName(normalized, 0);
  const mimeType = resolveDemandPreuveMime(
    sanitizeBlobMimeType(normalized.type),
    normalized.type,
    getDemandMediaType(normalized) === "video" ? "video/mp4" : "image/jpeg"
  );

  try {
    const buffer = await normalized.arrayBuffer();
    if (!buffer.byteLength) {
      logDemandPreuveUpload("materialize:reject", {
        reason: "empty_array_buffer",
        fileName,
        mimeType,
      });
      return null;
    }

    const materialized = createCapacitorSafeUploadFile([buffer], fileName, mimeType);
    if (!isValidPreparedUploadFile(materialized)) {
      logDemandPreuveUpload("materialize:reject", {
        reason: "invalid_after_safe_create",
        fileName,
        mimeType,
        ...describeUploadCandidate(materialized),
      });
      return null;
    }

    logDemandPreuveUpload("materialize:success", describeUploadCandidate(materialized));
    return materialized;
  } catch (error) {
    try {
      const cloned = toNativeBlobForFormData(normalized);
      if (!cloned.size) {
        logDemandPreuveUpload("materialize:reject", {
          reason: "clone_empty",
          fileName,
          mimeType,
          error,
        });
        return null;
      }
      const materialized = createCapacitorSafeUploadFile([cloned], fileName, mimeType);
      if (!isValidPreparedUploadFile(materialized)) {
        logDemandPreuveUpload("materialize:reject", {
          reason: "invalid_after_clone_safe_create",
          fileName,
          mimeType,
          ...describeUploadCandidate(materialized),
        });
        return null;
      }
      logDemandPreuveUpload("materialize:success_fallback_clone", {
        ...describeUploadCandidate(materialized),
      });
      return materialized;
    } catch (cloneError) {
      logDemandPreuveUpload("materialize:reject", {
        reason: "clone_failed",
        fileName,
        mimeType,
        error,
        cloneError,
      });
      return null;
    }
  }
}

/** Normalize demand preuve files to MIME types and extensions accepted by the API. */
export async function prepareDemandPreuveUploadFile(
  file: File
): Promise<File | null> {
  logDemandPreuveUpload("prepare:start", describeUploadCandidate(file));

  let candidate = file;
  if (isHeicLikeFile(candidate)) {
    logDemandPreuveUpload("prepare:heic_detected", describeUploadCandidate(candidate));
    const converted = await convertHeicLikeFileToJpeg(candidate);
    if (!converted) {
      logDemandPreuveUpload("prepare:reject", {
        reason: "heic_conversion_failed",
        ...describeUploadCandidate(candidate),
      });
      return null;
    }
    candidate = converted;
    logDemandPreuveUpload("prepare:heic_converted", describeUploadCandidate(candidate));
  }

  if (getDemandMediaType(candidate) === "image") {
    const fromCamera =
      (candidate as File & { __fromCamera?: boolean }).__fromCamera === true;
    const candidateSize = Number(candidate.size);
    const alreadyCompressedAtPick =
      fromCamera &&
      Number.isFinite(candidateSize) &&
      candidateSize > 0 &&
      candidateSize <= DEMAND_PREUVE_UPLOAD_MAX_BYTES;

    if (!alreadyCompressedAtPick) {
      candidate = await compressDemandPreuveImageIfNeeded(candidate, {
        force: fromCamera,
      });
    }
  }

  const materialized = await materializeUploadFile(normalizeDemandMediaFile(candidate));
  if (!materialized || !isValidPreparedUploadFile(materialized)) {
    logDemandPreuveUpload("prepare:reject", {
      reason: !materialized ? "materialize_failed" : "invalid_prepared_file",
      ...describeUploadCandidate(candidate),
      ...(materialized ? describeUploadCandidate(materialized) : {}),
    });
    return null;
  }

  const currentMeta = getUploadFileMetadata(materialized);
  const mimeType = resolveDemandPreuveMime(
    currentMeta.mimeType,
    sanitizeBlobMimeType(materialized.type),
    getDemandMediaType({
      name: currentMeta.fileName,
      type: currentMeta.mimeType,
    }) === "video"
      ? "video/mp4"
      : "image/jpeg"
  );
  const fileName = ensureBackendPreuveFilename(currentMeta.fileName, mimeType);

  if (fileName === currentMeta.fileName && currentMeta.mimeType === mimeType) {
    logDemandPreuveUpload("prepare:success", describeUploadCandidate(materialized));
    return materialized;
  }

  try {
    const buffer = await materialized.arrayBuffer();
    if (!buffer.byteLength) {
      logDemandPreuveUpload("prepare:reject", {
        reason: "final_empty_buffer",
        fileName,
        mimeType,
      });
      return null;
    }
    const prepared = createCapacitorSafeUploadFile([buffer], fileName, mimeType);
    if (!isValidPreparedUploadFile(prepared)) {
      logDemandPreuveUpload("prepare:reject", {
        reason: "invalid_after_rename",
        ...describeUploadCandidate(prepared),
      });
      return null;
    }
    logDemandPreuveUpload("prepare:success_renamed", describeUploadCandidate(prepared));
    return prepared;
  } catch (error) {
    logDemandPreuveUpload("prepare:reject", {
      reason: "final_buffer_failed",
      fileName,
      mimeType,
      error,
    });
    return null;
  }
}

export function isStartTimeBeforeEndTime(startTime: any, endTime: any) {
  function parseTime(timeString: string) {
    let [time, modifier]: any = timeString.split(" ");
    let [hours, minutes]: any = time.split(":");
    if (hours === "12") {
      hours = "00";
    }
    if (modifier === "PM") {
      hours = parseInt(hours, 10) + 12;
      hours = hours.toString();
    }
    return new Date(`1970-01-01T${hours.padStart(2, "0")}:${minutes}:00`);
  }

  const startDate = parseTime(startTime);
  const endDate = parseTime(endTime);

  return startDate < endDate;
}
export function getFileNameFromPath(filePath: string): string {
  return filePath.split("\\").pop()?.split("/").pop() || "";
}

export function requiresAuthenticatedPdfDownload(filePath?: string): boolean {
  if (!filePath) {
    return true;
  }

  const normalized = filePath.toLowerCase();
  return (
    (normalized.includes("/frontoffice/devis/") &&
      normalized.includes("/download")) ||
    normalized.includes("/api/auth/")
  );
}

export function buildDevisPdfFileName(devis?: {
  reference?: string;
  id?: number | string;
}): string {
  const reference = devis?.reference?.trim();
  if (reference) {
    return `${reference.replace(/\s+/g, "_")}.pdf`;
  }

  if (devis?.id != null) {
    return `devis_${devis.id}.pdf`;
  }

  return "devis.pdf";
}

export function buildFacturePdfFileName(demande?: {
  reference?: string;
  id?: number | string;
}): string {
  const reference = demande?.reference?.trim();
  if (reference) {
    return `facture_${reference.replace(/\s+/g, "_")}.pdf`;
  }

  if (demande?.id != null) {
    return `facture_${demande.id}.pdf`;
  }

  return "facture.pdf";
}
export function isEmpty(array: any) {
  let emptyItem = 0;
  array.map((val: any) => {
    if (val == "") {
      emptyItem++;
    }
  });
  return emptyItem === array.length;
}

export function isAppleRelayEmail(email: string): boolean {
  return email.endsWith("@privaterelay.appleid.com");
}

export function getZipCodeValidatorByCountry(getCountryCode: () => string): ValidatorFn {
  return (control: AbstractControl) => {
    const country = (getCountryCode() || '').toUpperCase();
    const value = control.value;
    if (!value) return null;

    // Patterns pour les pays européens (ISO 3166-1 alpha-2)
    const patterns: { [key: string]: RegExp } = {
      'FR': /^[0-9]{5}$/,                // France
      'DE': /^\d{5}$/,                   // Allemagne
      'IT': /^\d{5}$/,                   // Italie
      'ES': /^\d{5}$/,                   // Espagne
      'PT': /^\d{4}-?\d{3}$/,            // Portugal
      'BE': /^\d{4}$/,                   // Belgique
      'NL': /^\d{4}\s?[A-Za-z]{2}$/,     // Pays-Bas
      'LU': /^\d{4}$/,                   // Luxembourg
      'CH': /^\d{4}$/,                   // Suisse
      'AT': /^\d{4}$/,                   // Autriche
      'DK': /^\d{4}$/,                   // Danemark
      'SE': /^\d{3}\s?\d{2}$/,           // Suède
      'NO': /^\d{4}$/,                   // Norvège
      'FI': /^\d{5}$/,                   // Finlande
      'IS': /^\d{3}$/,                   // Islande
      'IE': /^[A-Za-z0-9]{3}\s?[A-Za-z0-9]{4}$/, // Irlande (Eircode)
      'GB': /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/, // Royaume-Uni
      'GR': /^\d{3}\s?\d{2}$/,           // Grèce
      'CY': /^\d{4}$/,                   // Chypre
      'MT': /^[A-Z]{3}\s?\d{4}$/,        // Malte
      'SI': /^\d{4}$/,                   // Slovénie
      'HR': /^\d{5}$/,                   // Croatie
      'HU': /^\d{4}$/,                   // Hongrie
      'CZ': /^\d{3}\s?\d{2}$/,           // Tchéquie
      'SK': /^\d{3}\s?\d{2}$/,           // Slovaquie
      'PL': /^\d{2}-\d{3}$/,             // Pologne
      'EE': /^\d{5}$/,                   // Estonie
      'LV': /^\d{4}$/,                   // Lettonie
      'LT': /^\d{5}$/,                   // Lituanie
      'RO': /^\d{6}$/,                   // Roumanie
      'BG': /^\d{4}$/,                   // Bulgarie
      'MD': /^\d{4}$/,                   // Moldavie
      // Ajoute d'autres pays si besoin
    };

    const pattern = patterns[country] || /.*/; // Par défaut, tout accepté
    return pattern.test(value) ? null : { invalidZip: true };
  };

}
  export function getSvgMarker(duration  :any){
    const label = duration.replace(/\s+/g, "");
    return  duration ?  `
     <svg version="1.1" id="Calque_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
      viewBox="0 0 84 160.5" style="enable-background:new 0 0 84 160.5;" xml:space="preserve">
    <style type="text/css">
      @font-face {
          font-family: 'Poppins';
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url(https://fonts.gstatic.com/s/poppins/v23/pxiByp8kv8JHgFVrLCz7Z11lFc-K.woff2) format('woff2');
          unicode-range: U+0900-097F, U+1CD0-1CF9, U+200C-200D, U+20A8, U+20B9, U+20F0, U+25CC, U+A830-A839, U+A8E0-A8FF, U+11B00-11B09;
          }
      .st0{fill:#FFFFFF;stroke:#3D8AD4;stroke-width:1.19;stroke-miterlimit:10;}
      .st1{fill:#3D8AD4;}
      .st2{fill:#EDEDED;}
      .st3{fill:#FFFFFF;}
      .st4{opacity:0.5;}
      .st5{font-family: 'Poppins', sans-serif;font-weight: bold;}
      .st6{font-size:24.5;}
    </style>
    <g>
      <path class="st0" d="M3.8,1.2h76.4c1.5,0,2.6,0.7,2.6,1.5v33.6c0,0.8-1.2,1.5-2.6,1.5H3.8c-1.5,0-2.6-0.7-2.6-1.5V2.7
        C1.1,1.9,2.3,1.2,3.8,1.2z"/>
      <polygon class="st1" points="49.2,84.5 36.5,84.5 42.4,160.5 	"/>
      <path class="st1" d="M77.2,66c0,18.8-15.3,34.1-34.1,34.1S9,84.8,9,66c0-18.8,15.3-34.1,34.1-34.1l0,0C61.9,31.9,77.2,47.2,77.2,66
        z"/>
      <path class="st2" d="M68.6,66c0,14.1-11.4,25.5-25.5,25.5C29,91.5,17.6,80.1,17.6,66S29,40.5,43.1,40.5
        C57.2,40.5,68.6,51.9,68.6,66L68.6,66z"/>
      <path class="st3" d="M65.2,66c0,12.2-9.9,22.1-22.1,22.1S21,78.2,21,66s9.9-22.1,22.1-22.1C55.3,43.9,65.2,53.8,65.2,66L65.2,66z"
        />
      <g class="st4">
        <path class="st3" d="M13.3,69.4c-0.5-3.1-0.6-6.3-0.1-9.4c0.4-3.2,1.4-6.2,2.8-9.1c1.4-2.9,3.4-5.5,5.9-7.6c2.5-2,5.4-3.5,8.5-4.2
          l0.1,0.2C28,41,25.6,42.9,23.4,45c-2.2,2-4.2,4.3-5.8,6.8c-1.5,2.6-2.6,5.5-3.3,8.4c-0.7,3-1,6.1-0.8,9.2H13.3z"/>
      </g>
      <path class="st1" d="M59,70.3L59,70.3v-1.1l0,0c0,0,0-0.4-0.5-0.4v0.4H27.7v-0.4c-0.5,0-0.5,0.4-0.5,0.4l0,0v1.2
        c-0.2,0.7,0,1.5,0.5,2.1c0.3,0.2,0.4,0.5,0.5,0.9h0.1v3.6c0,0,0,1.1,0.9,1.3h1.6c1.3,0,1.6-0.5,1.6-0.5c0.6-1.1,0.9-2.4,0.9-3.6
        c0-0.2,0-0.5,0-0.7h20c0,0.2,0,0.5,0,0.7c-0.1,1.3,0.2,2.5,0.8,3.7c0,0,0.3,0.6,1.6,0.5h1.6C58,78.1,58,77,58,77v-3.6
        c0,0,0-0.1,0-0.1c0.1-0.3,0.3-0.5,0.5-0.8C59,71.9,59.2,71.1,59,70.3z"/>
      <path class="st1" d="M58.2,64.7l-2.9-4.2l0,0l-0.2-0.3l0.2-0.7h2v-1.6h-2.7v1.4l-3.3-5.5H34.7l-3.8,6.2l-0.1-0.4h0.4v-1.6h-2.7v1.6
        h2l0.3,0.8l-0.1,0.1l0,0L28,64.7v4h30.2V64.7z M54.9,59.5l-0.1,0.2l-0.1-0.2H54.9z M35.8,54.7h14.4l3.1,5.1H32.5L35.8,54.7z
        M29.6,68.2h-0.8v-3h5.2l1.2,3H29.6z M50.4,68.2H35.9l-0.3-0.6h15L50.4,68.2z M50.7,67.3H35.6l-0.3-0.7h15.7L50.7,67.3z M57.4,68.2
        h-6.3l1.1-3h5.2L57.4,68.2z"/>
      <path class="st1" d="M47.5,56.6c-1.8,0-3.2,1.4-3.2,3.2h0.6c0-1.4,1.2-2.6,2.6-2.6c1.4,0,2.6,1.2,2.6,2.6h0.6
        C50.7,58,49.3,56.6,47.5,56.6z"/>
      <polygon class="st1" points="46.1,54 40.1,54 41.1,51.1 45.1,51.1 	"/>
      <foreignObject x="0" y="4" width="85" height="32" style="text-align: center; ">
        <div xmlns="http://www.w3.org/1999/xhtml" class="st5 st6" style="text-align: center;">
         ${label}
         </div>
      </foreignObject>
     </g>
    </svg>
    `  : ` <svg version="1.1" id="Calque_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
      viewBox="0 0 84 160.5" style="enable-background:new 0 0 84 160.5;" xml:space="preserve">
    <style type="text/css">
     
      .st0{fill:#FFFFFF;stroke:#3D8AD4;stroke-width:1.19;stroke-miterlimit:10;}
      .st1{fill:#3D8AD4;}
      .st2{fill:#EDEDED;}
      .st3{fill:#FFFFFF;}
      .st4{opacity:0.5;}
      .st6{font-size:24.4182px;}
    </style>
    <g>
     
      <polygon class="st1" points="49.2,84.5 36.5,84.5 42.4,160.5 	"/>
      <path class="st1" d="M77.2,66c0,18.8-15.3,34.1-34.1,34.1S9,84.8,9,66c0-18.8,15.3-34.1,34.1-34.1l0,0C61.9,31.9,77.2,47.2,77.2,66
        z"/>
      <path class="st2" d="M68.6,66c0,14.1-11.4,25.5-25.5,25.5C29,91.5,17.6,80.1,17.6,66S29,40.5,43.1,40.5
        C57.2,40.5,68.6,51.9,68.6,66L68.6,66z"/>
      <path class="st3" d="M65.2,66c0,12.2-9.9,22.1-22.1,22.1S21,78.2,21,66s9.9-22.1,22.1-22.1C55.3,43.9,65.2,53.8,65.2,66L65.2,66z"
        />
      <g class="st4">
        <path class="st3" d="M13.3,69.4c-0.5-3.1-0.6-6.3-0.1-9.4c0.4-3.2,1.4-6.2,2.8-9.1c1.4-2.9,3.4-5.5,5.9-7.6c2.5-2,5.4-3.5,8.5-4.2
          l0.1,0.2C28,41,25.6,42.9,23.4,45c-2.2,2-4.2,4.3-5.8,6.8c-1.5,2.6-2.6,5.5-3.3,8.4c-0.7,3-1,6.1-0.8,9.2H13.3z"/>
      </g>
      <path class="st1" d="M59,70.3L59,70.3v-1.1l0,0c0,0,0-0.4-0.5-0.4v0.4H27.7v-0.4c-0.5,0-0.5,0.4-0.5,0.4l0,0v1.2
        c-0.2,0.7,0,1.5,0.5,2.1c0.3,0.2,0.4,0.5,0.5,0.9h0.1v3.6c0,0,0,1.1,0.9,1.3h1.6c1.3,0,1.6-0.5,1.6-0.5c0.6-1.1,0.9-2.4,0.9-3.6
        c0-0.2,0-0.5,0-0.7h20c0,0.2,0,0.5,0,0.7c-0.1,1.3,0.2,2.5,0.8,3.7c0,0,0.3,0.6,1.6,0.5h1.6C58,78.1,58,77,58,77v-3.6
        c0,0,0-0.1,0-0.1c0.1-0.3,0.3-0.5,0.5-0.8C59,71.9,59.2,71.1,59,70.3z"/>
      <path class="st1" d="M58.2,64.7l-2.9-4.2l0,0l-0.2-0.3l0.2-0.7h2v-1.6h-2.7v1.4l-3.3-5.5H34.7l-3.8,6.2l-0.1-0.4h0.4v-1.6h-2.7v1.6
        h2l0.3,0.8l-0.1,0.1l0,0L28,64.7v4h30.2V64.7z M54.9,59.5l-0.1,0.2l-0.1-0.2H54.9z M35.8,54.7h14.4l3.1,5.1H32.5L35.8,54.7z
        M29.6,68.2h-0.8v-3h5.2l1.2,3H29.6z M50.4,68.2H35.9l-0.3-0.6h15L50.4,68.2z M50.7,67.3H35.6l-0.3-0.7h15.7L50.7,67.3z M57.4,68.2
        h-6.3l1.1-3h5.2L57.4,68.2z"/>
      <path class="st1" d="M47.5,56.6c-1.8,0-3.2,1.4-3.2,3.2h0.6c0-1.4,1.2-2.6,2.6-2.6c1.4,0,2.6,1.2,2.6,2.6h0.6
        C50.7,58,49.3,56.6,47.5,56.6z"/>
      <polygon class="st1" points="46.1,54 40.1,54 41.1,51.1 45.1,51.1 	"/>
       
    </g>
    </svg>`;

  }
